import EmergencyOverridesTable from './EmergencyOverridesTable';
import Header from '@components/common/layout/Header';
import ProvisioningActivityChart from './ProvisioningActivityChart';
import ProvisionsByProductChart from './ProvisionsByProductChart';
import React, {useMemo, useState} from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {TIMESPANS, getTimespanParams} from './timespanUtils';
import {useEmergencyOverrides} from '@hooks/data/admin-reports/useEmergencyOverrides';
import {useProvisioningReport} from '@hooks/data/admin-reports/useProvisioningReport';
import {useProvisionsByProduct} from '@hooks/data/admin-reports/useProvisionsByProduct';

const PAGE_SIZE = 50;

const SECTIONS = [
  {id: 'section-activity', label: 'Activity'},
  {id: 'section-products', label: 'By Product'},
  {id: 'section-emergencies', label: 'Emergencies'},
];

const scrollTo = id => {
  document
    .getElementById(id)
    ?.scrollIntoView({behavior: 'smooth', block: 'start'});
};

const AdminReportsPage = () => {
  const [timespan, setTimespan] = useState('30d');
  const [chartType, setChartType] = useState('bar');
  const [stacked, setStacked] = useState(false);
  const [metrics, setMetrics] = useState(['self_service', 'sfdc', 'manual']);
  const [overridesPage, setOverridesPage] = useState(0);
  const [productStatus, setProductStatus] = useState(null);
  // Trial orders (mostly free self-service) dominate the charts and drown out
  // real provisioning activity, so hide them by default — same as All Accounts.
  const [showTrials, setShowTrials] = useState(false);
  const isTrial = showTrials ? undefined : false;

  const {start, end, granularity} = useMemo(
    () => getTimespanParams(timespan),
    [timespan]
  );

  const {
    data: summaryData,
    isLoading: summaryLoading,
    error: summaryError,
  } = useProvisioningReport({start, end, granularity, isTrial});

  const {
    data: productData,
    isLoading: productLoading,
    error: productError,
  } = useProvisionsByProduct({start, end, status: productStatus, isTrial});

  const {
    data: overridesData,
    isLoading: overridesLoading,
    error: overridesError,
  } = useEmergencyOverrides({
    start,
    end,
    limit: PAGE_SIZE,
    offset: overridesPage * PAGE_SIZE,
  });

  const handleTimespanChange = ts => {
    setTimespan(ts);
    setOverridesPage(0);
  };

  return (
    <Box>
      <Header title="Provisioning Reports" />
      <Box sx={{px: 3, pt: 2, pb: 1}}>
        {/* Shared timespan + section jump — always visible below header */}
        <Stack
          direction={{xs: 'column', sm: 'row'}}
          alignItems={{xs: 'flex-start', sm: 'center'}}
          justifyContent="space-between"
          gap={1.5}
          mb={2}
        >
          <Stack direction="row" alignItems="center" gap={2}>
            <ToggleButtonGroup
              value={timespan}
              exclusive
              onChange={(_, v) => v && handleTimespanChange(v)}
              size="small"
            >
              {TIMESPANS.map(t => (
                <ToggleButton key={t.value} value={t.value}>
                  {t.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showTrials}
                  onChange={e => setShowTrials(e.target.checked)}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Show trials
                </Typography>
              }
            />
          </Stack>

          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography variant="caption" color="text.secondary" mr={0.5}>
              Jump to:
            </Typography>
            {SECTIONS.map(s => (
              <Button
                key={s.id}
                size="small"
                variant="text"
                onClick={() => scrollTo(s.id)}
                sx={{minWidth: 0, px: 1, fontSize: 12}}
              >
                {s.label}
              </Button>
            ))}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{px: 3, pb: 3}}>
        <Box id="section-activity">
          <ProvisioningActivityChart
            data={summaryData?.data}
            totals={summaryData?.totals}
            isLoading={summaryLoading}
            error={summaryError}
            timespan={timespan}
            chartType={chartType}
            onChartTypeChange={setChartType}
            stacked={stacked}
            onStackedChange={setStacked}
            metrics={metrics}
            onMetricsChange={setMetrics}
            granularity={granularity}
          />
        </Box>

        <Box id="section-products">
          <ProvisionsByProductChart
            data={productData?.data}
            isLoading={productLoading}
            error={productError}
            timespan={timespan}
            status={productStatus}
            onStatusChange={setProductStatus}
          />
        </Box>

        <Box id="section-emergencies">
          <EmergencyOverridesTable
            data={overridesData?.results}
            count={overridesData?.count}
            isLoading={overridesLoading}
            error={overridesError}
            start={start}
            end={end}
            page={overridesPage}
            pageSize={PAGE_SIZE}
            onPageChange={setOverridesPage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminReportsPage;
